// Time handling for the live2 board.
//
// The new Firestore documents hold native Firestore Timestamps, not ISO strings.
// `new Date(timestampObject)` yields an Invalid Date, and the old `hhmm()` then
// returned null — so times rendered BLANK rather than wrong, which is easy to
// miss in review. Everything time-shaped goes through `toMs` first.
//
// The dashboard reports Stuttgart operations, so the clock is Berlin time for
// every viewer regardless of where they are. A 'de-DE' locale sets FORMATTING
// only and leaves the timezone as the browser's, which rendered UK time under a
// German label when viewed from England. The timeZone option is what fixes it.

export const DISPLAY_TZ = 'Europe/Berlin';

/** Firestore Timestamp | Date | ISO string | epoch ms | null */
export type FsTime = any;

export function toMs(v: FsTime): number | null {
  if (v === null || v === undefined) return null;
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (v instanceof Date) {
    const t = v.getTime();
    return Number.isNaN(t) ? null : t;
  }
  // Firestore Timestamp
  if (typeof v?.toMillis === 'function') {
    try { return v.toMillis(); } catch { return null; }
  }
  if (typeof v?.toDate === 'function') {
    try {
      const t = v.toDate().getTime();
      return Number.isNaN(t) ? null : t;
    } catch { return null; }
  }
  // Timestamp-shaped plain object (e.g. after JSON round-trip)
  if (typeof v?.seconds === 'number') {
    return v.seconds * 1000 + Math.floor((v.nanoseconds ?? 0) / 1e6);
  }
  if (typeof v === 'string') {
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

/** "17:04" in Berlin time, or null when there is no usable value. */
export function hhmm(v: FsTime): string | null {
  const ms = toMs(v);
  if (ms === null) return null;
  return new Date(ms).toLocaleTimeString('de-DE', {
    timeZone: DISPLAY_TZ, hour: '2-digit', minute: '2-digit',
  });
}

/**
 * Delay to the nearest minute, rail convention: + late, - early.
 * null means not yet known, which is deliberately different from 0.
 */
export function delayLabel(sec?: number | null): string | null {
  if (sec === null || sec === undefined) return null;
  const mins = Math.round(sec / 60);
  if (mins === 0) return null;
  return `${mins > 0 ? '+' : ''}${mins}`;
}
